import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import CustomTable from "../../components/customTable/CustomTable";
import moment from "moment/moment";
import StatusChip from "../../theme/chip";
import DeleteConfirmation from "../../components/DeleteConfirmation";
import {
  _Delete_Support_Ticket,
  _Empty_Support_Ticket_Trash,
  _List_Support_Tickets_For_Admin,
  _Mark_Resolve_Support_Ticket,
} from "../../DAL/Listing/SupportTickets";
import { useAdminContext } from "../../Hooks/AdminContext";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));
function SupportTicketsTabs({
  tabValue,
  valueData,
  setOpen,
  setUserList,
  userList,
}) {
  const navigate = useNavigate();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [openDelete, setOpenDelete] = useState(false);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [pageCount, setPageCount] = useState(1);
  const [ticketId, setTicketId] = useState("");
  const [openResolved, setOpenResolved] = useState(false);
  const [openClearTrash, setOpenClearTrash] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [emptyTrash, setEmptyTrash] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const { socket } = useAdminContext();

  /**
   * getTicketsListing - Fetches and filters the list of support tickets based on the provided status and search criteria.
   *
   * This function sends a request to fetch support tickets data from the server, processes the response, and updates
   * the state with filtered ticket information, pagination data, and status counts. It also checks if any tickets are
   * in the "trash" status and updates the `emptyTrash` state accordingly.
   *
   * The filtering logic is as follows:
   * - **"answered"**: Filters tickets that are in "open" (ticket_status 0) and have a response (response_status 1).
   * - **"solved"**: Filters tickets that are resolved (ticket_status 1).
   * - **"trash"**: Filters tickets marked as "deleted" (ticket_status 2).
   * - **"waiting"**: Filters tickets that are open (ticket_status 0) and have no response (response_status 0).
   * - **Default**: If no specific status is selected, all tickets are returned.
   *
   * The function also formats the `createdAt` and `resolve_date` fields into a human-readable format.
   *
   * Upon successful response:
   * - The `userList` state is updated with the processed ticket data.
   * - The `open` state is updated with ticket counts for each status.
   * - The `totalCount` and `totalPages` are set for pagination.
   *
   * If the request fails, an error snackbar is shown.
   */
  const getTickestsListing = async (value, search) => {
    setIsLoading(true);
    const postData = {
      status: value || "all",
      search: search ? search.trim() : "",
    };
    const result = await _List_Support_Tickets_For_Admin(
      postData,
      page,
      rowsPerPage
    );
    if (result.code === 200) {
      let { total_count = {}, total_pages = {}, support_tickets = {} } = result;
      let data_value = support_tickets;
      const hasTrashStatus = support_tickets?.some(
        (ticket) => ticket.ticket_status === 2
      );

      if (hasTrashStatus) {
        setEmptyTrash(true);
      } else {
        setEmptyTrash(false);
      }

      if (valueData == "answered") {
        data_value = support_tickets?.filter(
          (item) => item.ticket_status == 0 && item.response_status == 1
        );
      } else if (valueData == "solved") {
        data_value = support_tickets?.filter((item) => item.ticket_status == 1);
      } else if (valueData == "trash") {
        data_value = support_tickets?.filter((item) => item.ticket_status == 2);
      } else if (valueData == "waiting") {
        data_value = support_tickets?.filter(
          (item) => item.ticket_status == 0 && item.response_status == 0
        );
      } else {
        data_value = support_tickets;
      }

      const data = data_value?.map((ticket) => {
        // Convert createdAt properly
        const support_ticket_date = moment(ticket.createdAt)
          .local()
          .format("YYYY-MM-DD HH:mm");
        const timeCreated = moment(ticket.createdAt).format(
          "DD MMM YY hh:mm A"
        );

        let resolvedData = "N/A";
        if (ticket.resolve_date) {
          resolvedData = moment(ticket.resolve_date).format(
            "DD MMM YY hh:mm A"
          );
        }
        return {
          ...ticket,
          timeCreated,
          resolvedData,
          MENU_OPTIONS: handleMenu(ticket),
        };
      });
      setOpen((prev) => ({
        ...prev,
        all: result?.all_count,
        waiting: result?.waiting_count,
        solved: result?.close_count,
        answered: result?.answered_count,
        trash: result?.trash_count,
      }));
      setTotalCount(total_count);
      setTotalPages(total_pages);
      setUserList(data);
      setIsLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  // This function handles page changes for pagination.
  // It updates the page state to the new page number and adjusts the page count accordingly.
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (newPage <= 0) {
      setPageCount(1);
    } else {
      setPageCount(newPage + 1);
    }
  };

  // This function handles changes in the number of rows per page for pagination.
  // It updates the rowsPerPage state to the new value and resets the page state to 0.
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // This function handles the page change for pagination.
  // If the new page is less than or equal to 0, it resets the page to 0 and sets the page count to 1.
  // Otherwise, it updates the page state to the new page and adjusts the page count accordingly.
  const handleChangePages = (event, newPage) => {
    if (newPage <= 0) {
      setPage(0);
      setPageCount(1);
    } else {
      setPage(newPage - 1);
      setPageCount(newPage);
    }
  };

  /**
   * hanleChangePage - Navigates to the support ticket details page when a ticket is clicked.
   *
   * This function deletes the `MENU_OPTIONS` property from the ticket object to ensure unnecessary data is not passed.
   * It then navigates to the support ticket details page using the `navigate` function and passes the selected ticket's
   * data (`value`) along with the current `tabValue` as state to the destination.
   *
   * @param {Object} value - The selected ticket object.
   */
  const hanleChangePage = (value) => {
    delete value.MENU_OPTIONS;
    navigate(`/support-ticket/${value._id}`, {
      state: { value, tabValue },
    });
  };

  /**
   * handleAgreeDelete - Sets the ticket ID and opens the delete confirmation modal.
   *
   * This function is triggered when the user agrees to delete a support ticket. It sets the `ticketId` with the selected
   * ticket's ID (`value`) and then opens the delete confirmation modal by setting the `openDelete` state to true.
   *
   * @param {Object} value - The ticket object, containing the ID to be deleted.
   */
  const handleAgreeDelete = (value) => {
    setTicketId(value);
    setOpenDelete(true);
  };

  /**
   * handleMarkResolvedPopup - Sets the ticket ID and opens the resolved confirmation modal.
   *
   * This function is triggered when the user marks a support ticket as resolved. It sets the `ticketId` with the selected
   * ticket's ID (`value`) and then opens the resolved confirmation modal by setting the `openResolved` state to true.
   *
   * @param {Object} value - The ticket object, containing the ID to be marked as resolved.
   */
  const handleMarkResolvedPopup = (value) => {
    setTicketId(value);
    setOpenResolved(true);
  };

  const TABLE_HEAD = [
    {
      id: "action",
      label: "Action",
      type: "action",
      MENU_OPTIONS: "MENU_OPTIONS",
    },
    {
      id: "number",
      label: "#",
      type: "number",
    },
    {
      id: "subject",
      label: "Subject",
      renderData: (data) => {
        const truncatedSubject =
          data.subject.length > 40
            ? data.subject.substring(0, 40) + "..."
            : data.subject;
        return (
          <Tooltip title="View Details">
            <span className="pointer" onClick={() => hanleChangePage(data)}>
              {truncatedSubject}
            </span>
          </Tooltip>
        );
      },
    },
    { id: "timeCreated", label: "Created At" },
    (tabValue === 0 || tabValue === 3 || tabValue === 4) && {
      id: "resolvedData",
      label: "Resolved Date",
    },
    {
      id: "added_by",
      label: " Added By",
      renderData: (data) => {
        return (
          <>
            <Stack direction="column" alignItems="start" spacing={0}>
              {data.user_info && (
                <>
                  <Typography variant="subtitle2" sx={{ fontSize: 12 }} noWrap>
                    {data?.user_info?.first_name +
                      " " +
                      data?.user_info?.last_name}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 11, opacity: 0.7 }}
                    variant="body2"
                    noWrap
                  >
                    {data?.user_info?.email}
                  </Typography>
                </>
              )}
            </Stack>
          </>
        );
      },
    },
    {
      id: "ticket_status",
      label: "Status",
      renderData: (data) => {
        let ticket_status = data.ticket_status;
        let response_status = data.response_status;
        return (
          <StatusChip
            status={
              ticket_status === 0 && response_status == 1
                ? "Answered"
                : ticket_status == 1
                ? "Closed"
                : ticket_status == 2
                ? "Trash"
                : "waiting"
            }
            type="activeInactive"
          />
        );
      },
    },
  ];

  /**
   * handleMenu - Generates menu options based on the status of a support ticket.
   *
   * This function checks the `ticket_status` of a row (support ticket) and returns a menu with specific actions:
   * - If the ticket is resolved (`ticket_status === 1`) or in trash (`ticket_status === 2`), it shows a "Detail" option.
   * - If the ticket is not resolved and not in trash, it also includes "Delete" and "Mark Resolved" options.
   * - The "Mark Resolved" option is excluded if the ticket is already in trash (`ticket_status === 2`).
   *
   * @param {Object} row - The ticket object containing the `ticket_status`.
   * @returns {Array} The menu options for the ticket.
   */
  const handleMenu = (row) => {
    let MENU_OPTIONS = null;
    if (row.ticket_status === 1 || row.ticket_status === 2) {
      MENU_OPTIONS = [
        {
          label: "Detail",
          icon: "gg:details-more",
          handleClick: hanleChangePage,
        },
        row.ticket_status !== 2 && {
          label: "Delete",
          icon: "ant-design:delete-twotone",
          handleClick: handleAgreeDelete,
        },
      ];
    } else {
      MENU_OPTIONS = [
        {
          label: "Detail",
          icon: "gg:details-more",
          handleClick: hanleChangePage,
        },
        {
          label: "Delete",
          icon: "ant-design:delete-twotone",
          handleClick: handleAgreeDelete,
        },
        {
          label: "Mark Resolved",
          icon: "emojione-monotone:heavy-check-mark",
          handleClick: handleMarkResolvedPopup,
        },
      ];
    }
    return MENU_OPTIONS;
  };

  const handleDelete = async () => {
    setButtonLoading(true);
    const result =
      ticketId.ticket_status === 2
        ? await _Delete_Support_Ticket(ticketId._id)
        : await _Delete_Support_Ticket(ticketId._id);
    if (result.code === 200) {
      socket.emit("support_ticket_event", {
        notification: result.notification,
      });
      getTickestsListing(valueData, filterName);
      enqueueSnackbar("Support Ticket Deleted Successfully", {
        variant: "success",
      });
      setOpenDelete(false);
      setButtonLoading(false);
    } else {
      setButtonLoading(false);
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handleMarkResolved = async () => {
    setButtonLoading(true);

    const result = await _Mark_Resolve_Support_Ticket(ticketId._id);
    if (result.code === 200) {
      socket.emit("support_ticket_event", {
        notification: result.notification,
      });
      setOpenResolved(false);
      getTickestsListing(valueData, filterName);
      enqueueSnackbar(result.message, { variant: "success" });
      setButtonLoading(false);
    } else {
      setButtonLoading(false);
      enqueueSnackbar(result.message, { variant: "error" });
      navigate(`/support-ticket`);
    }
  };

  const handleClear = () => {
    setOpenClearTrash(true);
  };

  const handleClearTrash = async () => {
    setButtonLoading(true);

    const result = await _Empty_Support_Ticket_Trash();
    if (result.code === 200) {
      setOpenClearTrash(false);
      getTickestsListing(valueData, filterName);
      enqueueSnackbar(result.message, { variant: "success" });
      setButtonLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setButtonLoading(false);
    }
  };

  const searchFunction = (e) => {
    if (e) {
      e.preventDefault();
    }
    setPage(0);
    setPageCount(1);
    getTickestsListing(valueData, filterName);
    localStorage.setItem("support_tickets_data", filterName);
  };

  useEffect(() => {
    let search_text = localStorage.getItem("support_tickets_data");
    if (search_text) {
      setFilterName(search_text);
    }
    getTickestsListing(valueData, search_text);
  }, [rowsPerPage, page, valueData]);

  if (isLoading) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <div className="container-fluid mt-4 p-0 ">
      <CustomTable
        TABLE_HEAD={TABLE_HEAD}
        data={userList}
        custom_pagination={{
          total_count: totalCount,
          rows_per_page: rowsPerPage,
          page: page,
          handleChangePage: handleChangePage,
          onRowsPerPageChange: handleChangeRowsPerPage,
        }}
        custom_search={{
          searchText: filterName,
          setSearchText: setFilterName,
          handleSubmit: searchFunction,
        }}
        pageCount={pageCount}
        totalPages={totalPages}
        handleChangePages={handleChangePages}
        rows_per_page_count={rowsPerPage}
        pagePagination={true}
        show_extra_button={
          userList.length > 0 && {
            isShow: valueData === "trash",
            handleClick: handleClear,
            title: "Clear Trash",
          }
        }
        placeHoldertext={"Search by subject"}
      />
      <DeleteConfirmation
        isLoading={buttonLoading}
        open={openDelete}
        handleClose={() => setOpenDelete(false)}
        setOpen={setOpenDelete}
        title={"Are you sure you want to delete this Ticket?"}
        handleAgree={handleDelete}
        Btn_Title={"Delete"}
      />
      <DeleteConfirmation
        isLoading={buttonLoading}
        open={openResolved}
        setOpen={setOpenResolved}
        handleClose={() => setOpenResolved(false)}
        title={"Are you sure you want to mark this ticket as resolved?"}
        handleAgree={handleMarkResolved}
        Btn_Title={"Agree"}
      />
      <DeleteConfirmation
        isLoading={buttonLoading}
        open={openClearTrash}
        setOpen={setOpenClearTrash}
        handleClose={() => setOpenClearTrash(false)}
        Btn_Title={"Clear"}
        title={"Are you sure you want to clear your trash tickets?"}
        handleAgree={handleClearTrash}
      />
    </div>
  );
}
export default SupportTicketsTabs;
